import {createSyncQueue} from '../offline/queue.js';
import {SyncStatus} from '../offline/entity.js';
import {isPortError} from '../platform/port-error.js';
import {
  FEEDBACK_ENTITY_TYPE,
  FeedbackDeliveryStatus,
  FeedbackKind,
  createFeedbackSubmission
} from './model.js';

function ownerId(value){
  const id=String(value??'').trim();
  if(!id)throw new TypeError('feedback owner userId is required');
  return id.slice(0,128);
}

function opaqueId(createId){
  const raw=String(createId?.()||globalThis.crypto?.randomUUID?.()||Date.now()).trim();
  const safe=raw.replace(/[^A-Za-z0-9_.:-]/g,'').slice(0,71);
  return 'feedback_'+(safe||String(Date.now()));
}

function online(value){
  try{return Boolean(value());}catch{return false;}
}

function record(diagnostics,event){
  try{diagnostics?.record?.(event);}catch{}
}

export function createFeedbackService({
  userId,
  repository,
  remote,
  diagnostics,
  diagnosticBundleProvider,
  attachmentProvider=null,
  now=()=>new Date().toISOString(),
  createId=()=>globalThis.crypto?.randomUUID?.()||String(Date.now()),
  isOnline=()=>typeof navigator==='undefined'?true:navigator.onLine
}={}){
  if(!repository?.get||!repository?.put||!repository?.remove||!repository?.listPending){
    throw new TypeError('feedback repository is required');
  }
  if(typeof remote?.submit!=='function')throw new TypeError('feedback remote submit() is required');
  const owner=ownerId(userId);

  const queue=createSyncQueue({repository,now});
  const inFlight=new Map();

  function createRequestId(){
    return opaqueId(createId);
  }

  function diagnosticBundle({kind,includeDiagnostics,correlationId,currentScreen,currentModule}){
    if(String(kind||'').trim().toLowerCase()!==FeedbackKind.BUG||includeDiagnostics===false)return null;
    try{return diagnosticBundleProvider?.create?.({
      correlationId,
      screen:currentScreen,
      module:currentModule
    })??diagnostics?.exportSafe?.()??null;}
    catch(error){
      try{diagnostics?.capture?.('feedback.diagnostic-bundle',error);}catch{}
      return null;
    }
  }

  async function persist(input){
    const timestamp=String(now());
    if(input.userId!=null&&String(input.userId).trim()!==owner){
      throw new TypeError('feedback submission owner mismatch');
    }
    const submission=createFeedbackSubmission({
      ...input,
      userId:owner,
      requestId:input.requestId||createRequestId(),
      createdAt:input.createdAt||timestamp,
      updatedAt:timestamp,
      diagnosticBundle:diagnosticBundle(input)
    });
    return queue.enqueue(FEEDBACK_ENTITY_TYPE,submission,'create');
  }

  async function deliver(entity,{signal}={}){
    const attempted=await queue.markAttempt(FEEDBACK_ENTITY_TYPE,entity);
    try{
      const response=await remote.submit(attempted,{signal});
      await repository.remove(FEEDBACK_ENTITY_TYPE,attempted.id);
      record(diagnostics,{
        module:'feedback',
        eventCode:'feedback.submit.success',
        outcome:'success',
        correlationId:attempted.diagnosticBundle?.context?.correlation_id??null,
        meta:{kind:attempted.kind,status:response?.status||'submitted'}
      });
      return Object.freeze({
        status:'submitted',
        requestId:attempted.requestId,
        duplicate:Boolean(response?.duplicate)
      });
    }catch(error){
      const code=String(error?.code||'FEEDBACK_SUBMIT_FAILED').slice(0,80);
      if(isPortError(error)&&error.retryable===false){
        await repository.put(FEEDBACK_ENTITY_TYPE,{
          ...attempted,
          syncStatus:SyncStatus.SYNCED,
          deliveryStatus:FeedbackDeliveryStatus.FAILED,
          lastSyncFailureAt:String(now()),
          lastSyncErrorCode:code,
          nextSyncAttemptAt:null
        });
        record(diagnostics,{
          severity:'error',
          module:'feedback',
          eventCode:code,
          outcome:'failure',
          meta:{kind:attempted.kind,retryable:false}
        });
        return Object.freeze({status:'failed',requestId:attempted.requestId,errorCode:code});
      }

      const pending=await queue.markFailure(FEEDBACK_ENTITY_TYPE,attempted,code);
      record(diagnostics,{
        severity:'warn',
        module:'feedback',
        eventCode:code,
        outcome:'partial',
        meta:{kind:attempted.kind,retryable:true}
      });
      return Object.freeze({
        status:'queued',
        requestId:pending.requestId,
        errorCode:code,
        nextAttemptAt:pending.nextSyncAttemptAt
      });
    }
  }

  async function submit(input={}){
    const requestId=input.requestId||createRequestId();
    if(inFlight.has(requestId))return inFlight.get(requestId);

    const task=(async()=>{
      const existing=await repository.get(FEEDBACK_ENTITY_TYPE,requestId);
      if(existing&&existing.userId!==owner)throw new TypeError('feedback submission owner mismatch');
      const entity=existing??await persist({...input,requestId});
      if(!online(isOnline)){
        record(diagnostics,{
          severity:'info',
          module:'feedback',
          eventCode:'feedback.submit.queued_offline',
          outcome:'offline',
          networkState:'offline',
          meta:{kind:entity.kind}
        });
        return Object.freeze({status:'queued',requestId:entity.requestId});
      }
      return deliver(entity,{signal:input.signal});
    })();

    inFlight.set(requestId,task);
    try{return await task;}
    finally{inFlight.delete(requestId);}
  }

  async function retryPending(){
    if(!online(isOnline))return Object.freeze({status:'offline',processed:0,submitted:0,queued:0,failed:0});
    const ready=(await queue.listReady()).filter(row=>
      row?.entityType===FEEDBACK_ENTITY_TYPE
      &&row.userId===owner
      &&row.deliveryStatus!==FeedbackDeliveryStatus.FAILED
    );
    let submitted=0,queued=0,failed=0;
    const results=[];
    for(const entity of ready){
      try{
        const result=await deliver(entity);
        results.push(result);
        if(result.status==='submitted')submitted+=1;
        else if(result.status==='failed')failed+=1;
        else queued+=1;
      }catch(error){
        failed+=1;
        results.push(Object.freeze({
          status:'failed',
          requestId:entity.requestId,
          errorCode:String(error?.code||'FEEDBACK_RETRY_FAILED').slice(0,80)
        }));
        try{diagnostics?.capture?.('feedback.retry',error,{kind:entity.kind});}catch{}
      }
    }
    return Object.freeze({
      status:'complete',
      processed:ready.length,
      submitted,
      queued,
      failed,
      results:Object.freeze(results)
    });
  }

  async function cancel(requestId){
    const id=String(requestId||'').trim();
    if(!id)return false;
    const existing=await repository.get(FEEDBACK_ENTITY_TYPE,id);
    if(!existing||existing.userId!==owner)return false;
    if(existing.attachment){
      if(typeof attachmentProvider?.remove!=='function')return false;
      try{
        await attachmentProvider.remove({userId:owner,attachment:existing.attachment});
      }catch(error){
        try{diagnostics?.capture?.('feedback.attachment.cleanup',error,{kind:existing.kind});}catch{}
        return false;
      }
    }
    await repository.remove(FEEDBACK_ENTITY_TYPE,id);
    record(diagnostics,{
      module:'feedback',
      eventCode:'feedback.draft.cancelled',
      outcome:'cancelled',
      meta:{kind:existing.kind}
    });
    return true;
  }

  async function pending(){
    const rows=await queue.listPending();
    return Object.freeze(rows.filter(row=>row?.entityType===FEEDBACK_ENTITY_TYPE&&row.userId===owner));
  }

  return Object.freeze({createRequestId,submit,retryPending,cancel,pending});
}
